import { requester } from "App";
import Dummy from "components/Dummy";
import P from "components/P";
import Container from "layouts/container/Container";
import FlexChild from "layouts/flex/FlexChild";
import VerticalFlex from "layouts/flex/VerticalFlex";
import { AuthContext } from "providers/AuthProvider";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getTokenPayload } from "shared/utils/Utils";

function PaymentGateway() {
    const location = useLocation();
    const [isMounted, setMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingStep, setProcessingStep] = useState("초기화 중");

    const navigate = useNavigate();
    const { token } = useSelector((state) => ({
        token: state.auth.token,
    }));

    const isDev = process.env.REACT_APP_ENV === "development";

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!isMounted) return;

        const queryParams = new URLSearchParams(location.search);
        const type = queryParams.get('type');
        const trxId = queryParams.get('trxId');
        const orderId = queryParams.get('orderId');
        const paymentKey = queryParams.get('paymentKey');
        const amount = queryParams.get('amount');
        const usedPoints = queryParams.get('usedPoints');
        const storeCode = queryParams.get('storeCode');
        const machineData = queryParams.get('data');
        const deviceCodeData = queryParams.get('deviceCode');

        try {
            // 필수 파라미터 검증
            if (!type || !orderId || !storeCode) {
                throw new Error("필수 결제 정보가 누락되었습니다");
            }

            let tokenPayload = getTokenPayload(token);
            let userId = tokenPayload?.userId;

            if (!userId) {
                throw new Error("사용자 인증 정보를 찾을 수 없습니다");
            }

            setProcessingStep("결제 상태 업데이트 중");

            if (type === "point") {
                let data = {
                    id: orderId,
                    price: amount
                };

                requester.pointRecharge(data, (result) => {
                    if (result.code === 0) {
                        // 포인트 충전 성공 처리
                        // navigate("/ws/root/complete");
                    } else {
                        setError("포인트 충전 처리 중 오류가 발생했습니다");
                        setIsLoading(false);
                    }
                });
            } else if (type === "product") {
                let productData = {
                    id: orderId,
                    totalAmount: amount,
                    usedPoints: usedPoints,
                    userId: userId,
                    trxId: trxId,
                    storeCode: storeCode
                };

                // 진행 상태 업데이트
                setProcessingStep("주문 상태 업데이트 중");

                if (isDev) {
                    requester.testChangeOrderStatus(productData, (result) => {
                        if (result.code === 0) {
                            setProcessingStep("주문 배출 준비 중");
                            window.location.replace(`/ws/${storeCode}/payment/complete?data=${machineData}&deviceCode=${deviceCodeData}&orderId=${orderId}`);
                        } else {
                            setError("결제 처리 중 오류가 발생했습니다");
                            setIsLoading(false);
                            setTimeout(() => {
                                window.location.replace(`/ws/${storeCode}/fail`);
                            }, 2000);
                        }
                    });
                } else {
                    requester.paymentChangeOrderStatus(productData, (result) => {
                        if (result.code === 0) {
                            setProcessingStep("주문 배출 준비 중");
                            window.location.replace(`/ws/${storeCode}/payment/complete?data=${machineData}&deviceCode=${deviceCodeData}&orderId=${orderId}`);
                        } else {
                            setError("결제 처리 중 오류가 발생했습니다");
                            setIsLoading(false);
                            setTimeout(() => {
                                window.location.replace(`/ws/${storeCode}/fail`);
                            }, 2000);
                        }
                    });
                }
            } else {
                throw new Error("알 수 없는 결제 유형입니다");
            }
        } catch (error) {
            console.error("결제 처리 중 오류:", error);
            setError(error.message || "결제 처리 중 오류가 발생했습니다");
            setIsLoading(false);
        }
    }, [isMounted, token, navigate, isDev, location.search]);

    // 오류 화면 렌더링
    if (error) {
        return (
            <Container height={"85vh"}>
                <VerticalFlex>
                    <FlexChild justifyContent={"center"}>
                        <P size={18} weight={"bold"} color="red">결제 처리 중 오류가 발생했습니다</P>
                        <Dummy height={"15px"} />
                        <P size={16}>{error}</P>
                        <Dummy height={"30px"} />
                        <P size={14} color="#666">잠시 후 자동으로 이동합니다...</P>
                    </FlexChild>
                </VerticalFlex>
            </Container>
        );
    }

    // 로딩 화면 렌더링
    return (
        <Container height={"85vh"}>
            <VerticalFlex>
                <FlexChild justifyContent={"center"}>
                    <P size={20} weight={"bold"} color="var(--main-color)">결제 처리 중입니다</P>
                    <Dummy height={"15px"} />
                    <P size={16} weight={"medium"}>{processingStep}</P>
                    <Dummy height={"30px"} />

                    {/* 로딩 스피너 */}
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <style>{`
                            .loading-container {
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                margin: 20px 0;
                            }
                            .loading-spinner {
                                width: 50px;
                                height: 50px;
                                border: 4px solid rgba(0, 0, 0, 0.1);
                                border-radius: 50%;
                                border-top: 4px solid var(--main-color);
                                animation: spin 1s linear infinite;
                            }
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                            @keyframes fade {
                                0% { opacity: 0.4; }
                                50% { opacity: 1; }
                                100% { opacity: 0.4; }
                            }
                            .loading-dots span {
                                animation: fade 1.4s infinite;
                                animation-fill-mode: both;
                            }
                            .loading-dots span:nth-child(2) {
                                animation-delay: 0.2s;
                            }
                            .loading-dots span:nth-child(3) {
                                animation-delay: 0.4s;
                            }
                        `}</style>
                    </div>

                    <Dummy height={"20px"} />

                    {/* 로딩 점 애니메이션 */}
                    <P size={14} color="#666" className="loading-dots">
                        처리 중입니다
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </P>

                    <Dummy height={"50px"} />
                    <P size={12} color="#999">결제 처리 중에는 화면을 닫지 마세요.</P>
                </FlexChild>
            </VerticalFlex>
        </Container>
    );
}

export default PaymentGateway;